import React, {useMemo} from "react";
import {useTranslate} from "@tolgee/react";
import Drawer from "@material-ui/core/Drawer";
import Box from "@material-ui/core/Box";
import Link from "@material-ui/core/Link";
import {useSelector} from "react-redux";
import {selectUser} from "store/auth/selectors";
import {selectMetadata} from "store/metadata/selectors";
import useBranding, {Branding} from "store/branding/branding.store";
import {useCurrentShowId} from "hooks";
import {Theme} from "@evennt-tech/api-v2-core";
import useViewMode, {ViewMode} from "store/viewMode/viewMode.store";
import {FFairPrimaryGreyLogo} from "view/images/FFairLogo";
import FlexBox from "view/components/Layout/FlexBox";
import SupplierNavigation from "./SupplierNavigation";
import AdminNavigation from "./AdminNavigation";
import useStyles from "./style";
import MyFrillWidget from "./FrillWidget";
import OrganiserNavigation from "./OrganiserNavigation";
import ExhibitorNavigation from "./ExhibitorNavigation";
import ShowLanguagePicker from "../LanguagePicker/ShowLanguagePicker";

type Props = {
  topSpacing: number;
};

const NavBar: React.FC<Props> = ({topSpacing}) => {
  const classes = useStyles({});
  const {t} = useTranslate();
  const showId = useCurrentShowId();
  const {role} = useSelector(selectUser);
  const {hideSidebar} = useSelector(selectMetadata);
  const {getBranding} = useBranding();
  const {mode} = useViewMode();
  const branding = getBranding(showId);

  const helpPortalUrl = useMemo(() => {
    switch (role) {
      case "organiser": {
        return "https://support.ffair.io/organiser-knowledge-base";
      }
      case "exhibitor": {
        return "https://support.ffair.io/exhibitor-knowledge-base";
      }
      case "supplier": {
        return "https://support.ffair.io/supplier-knowledge-base";
      }
      default: {
        return "#";
      }
    }
  }, [role]);

  if (!role || hideSidebar) {
    return null;
  }

  const background =
    role === "exhibitor" || mode === ViewMode.EXHIBITOR
      ? getBackground(branding)
      : undefined;

  return (
    <Drawer
      className={classes.drawer}
      variant="permanent"
      classes={{
        paper: classes.drawerPaper,
      }}
      PaperProps={{
        style: {paddingTop: topSpacing, display: "flex", background},
      }}
    >
      <Box className={classes.drawerContainer}>
        {role === "admin" && <AdminNavigation />}
        {role === "organiser" && mode !== ViewMode.EXHIBITOR && (
          <OrganiserNavigation />
        )}
        {role === "supplier" && <SupplierNavigation />}
        {(role === "exhibitor" || mode === ViewMode.EXHIBITOR) && (
          <ExhibitorNavigation />
        )}
      </Box>
      <Box>
        <Box className={classes.logo}>
          <Box display="flex" alignItems="center">
            <span className={classes.poweredByText}>Powered by&nbsp;</span>
            <FFairPrimaryGreyLogo />
          </Box>
        </Box>
        <Box className={classes.links}>
          <Box className={classes.linkRow}>
            <Link href={helpPortalUrl} className={classes.link} target="_blank">
              {t("common.nav.help_support")}
            </Link>
            <MyFrillWidget />
          </Box>
          {(role === "exhibitor" || mode === ViewMode.EXHIBITOR) && (
            <FlexBox alignCenter justifyCenter mt={2}>
              <ShowLanguagePicker />
            </FlexBox>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

const getBackground = (branding: Branding) => {
  const opacity = (branding?.leftSideMenuOpacity || 100) / 100;
  let color = `rgba(255, 255, 255, ${opacity})`;
  if (branding?.theme === Theme.DARK) {
    color = `rgba(39, 41, 46, ${opacity})`;
  }

  return color;
};

export default NavBar;
